/**
 * Internal School Feature-Config API Contract (SCH-1665)
 *
 * PATCH /internal/schools/:id/config/global-features/:feature
 * PATCH /internal/schools/:id/config/screen-features/:screen
 *
 * Both are `checkApiKeyMiddleware` routes — the internal siblings of the
 * Clerk-authed `/v1/system/app-config/*` writers. Those are the only existing
 * write path for a school's `SystemConfiguration` feature rows, and an agent
 * carries no browser session, so the alternative to these two verbs was hand-
 * written SQL against production or minting a console token. Both of those are
 * worse than an endpoint whose blast radius is one school and one named block.
 *
 * THE BODY IS A SPARSE PATCH, NOT A REPLACEMENT. This is the property the whole
 * contract is built around: an operator writing `{"teacherAttendanceState":
 * "live"}` must not be able to erase the rest of that school's attendance
 * config by omission. Riverstone lost its entire parent-absence policy for four
 * days to exactly that failure on the "update all" route (see
 * `updateAllGlobalFeatures`), and the fix there was the same one encoded here:
 * an omitted key means "leave it alone", never "delete it".
 *
 * Feature-specific validation is deliberately NOT repeated here. The two config
 * services already validate the touched block against its own schema, and a
 * second copy of those rules in the wire contract would be a second thing to
 * keep in step. What this file validates is the shape the services cannot see:
 * that the body is a non-empty object at all.
 *
 * The two services differ in one way worth knowing before you write a body.
 * Global features persist a SPARSE override row, so any subset of a feature's
 * fields is a valid patch. Screen features persist the MERGED config, which
 * readers treat as complete — so patching an EXISTING feature is fully sparse
 * (`{"features":{"schedule":{"enabled":false}}}` keeps its `access`), but a
 * patch that CREATES a feature must carry that feature's required fields
 * (`{"features":{"businessPhone":{"enabled":true,"access":"*"}}}`). The
 * rejection names the missing path.
 */

import { z } from 'zod'
import { GlobalFeaturesConfigSchema } from '../../schemas/entities/global-features'
import { ScreensConfigSchema } from '../../schemas/entities/screen-features'

/**
 * Every key the root global-features schema defines, derived from the schema
 * rather than hand-listed. A hand-written copy is how `startScreen` and
 * `app-availability` would each have shipped writable-but-unreadable — the
 * backend's own `GLOBAL_FEATURE_SCHEMAS` map carries a long comment about that
 * exact failure. Deriving costs one `Object.keys` at module load and cannot
 * drift.
 */
export const GLOBAL_FEATURE_NAMES = Object.keys(GlobalFeaturesConfigSchema.shape) as (keyof z.infer<
  typeof GlobalFeaturesConfigSchema
>)[]

/**
 * `media-quality` is READ-TIME INJECTED, never persisted as a school override:
 * the media-quality resolver writes it into the merged config on every read,
 * and `updateGlobalFeaturePartial` explicitly strips it back out before saving
 * (`stripInjectedGlobalFeatureFields`). So a patch aimed at it would be
 * accepted, silently dropped, and then read back looking applied — because the
 * resolver re-injects the value the operator thinks they just wrote.
 *
 * Refusing it at the door is the difference between a confusing 400 out of the
 * service's lenient-parse (`expected object, received undefined`) and a 400
 * that says which knob to turn instead.
 */
export const READ_ONLY_GLOBAL_FEATURE_NAMES = ['media-quality'] as const

/**
 * Blocks only a super-admin SESSION may write, so these keyed doors refuse
 * them. `INTERNAL_API_KEY` is held by CI, services and engineers' laptops; the
 * parent AI chat setting (SCH-5216) opens a new data path to parents, so a
 * holder of that key must not be able to turn it on (or off) at a school. The
 * super-admin features panel (`/v1/system/app-config/global-features/*`) is
 * its only writer.
 */
export const SUPER_ADMIN_ONLY_GLOBAL_FEATURE_NAMES = ['parentAiChat'] as const

/** The global-feature blocks this endpoint will actually persist. */
export const WRITABLE_GLOBAL_FEATURE_NAMES = GLOBAL_FEATURE_NAMES.filter(
  name =>
    !(READ_ONLY_GLOBAL_FEATURE_NAMES as readonly string[]).includes(name) &&
    !(SUPER_ADMIN_ONLY_GLOBAL_FEATURE_NAMES as readonly string[]).includes(name)
)

/** Every screen the root screens schema defines. Derived, for the reason above. */
export const SCREEN_NAMES = Object.keys(ScreensConfigSchema.shape) as (keyof z.infer<typeof ScreensConfigSchema>)[]

/**
 * The request body for BOTH verbs: a sparse patch object.
 *
 * NON-EMPTY IS ENFORCED, and for the same reason the Slack-channel contract
 * refuses an empty body: an empty patch is a successful no-op, and so is a body
 * whose only key was misspelled at the top level. An operator running this
 * against eight schools by hand must not be able to read a 200 as "the flag is
 * on". A caller who only wants to READ the block has
 * `GET /v1/system/app-config/*`.
 *
 * `z.record` rejects arrays and `null` (both parse as a non-object type), so
 * `[]` and `null` bodies fail here rather than reaching the merge as something
 * that would replace the block wholesale.
 */
export const SchoolConfigPatchSchema = z
  .record(z.string(), z.unknown())
  .refine(patch => Object.keys(patch).length > 0, {
    message:
      'Send a non-empty patch object. An empty body would write nothing and still answer 200 — as would a body whose only key was misspelled.'
  })
export type SchoolConfigPatch = z.infer<typeof SchoolConfigPatchSchema>

/**
 * Response for both verbs — the block AS THE APP NOW RESOLVES IT, re-read from
 * the primary past every cache rather than echoed from the request.
 *
 * That is the MERGED view (system default + this school's override, plus any
 * read-time injection), not the raw override row. It is deliberately the more
 * useful of the two: an operator flipping a flag is asking "what will mobile see
 * now", and the override row alone cannot answer that — a key the school does
 * not override is absent from it whether the default is on or off. The raw row
 * is still the thing that was WRITTEN; `updateGlobalFeaturePartial` persists
 * only the patch, and this response does not change that.
 *
 * That distinction is load-bearing on the screen-features side. The service
 * memoizes the merged config per school for 60s in-process and the underlying
 * read hits a replica, which is the documented stale-read-after-write that had
 * a St. Mark's admin re-saving the same toggle five times (see
 * `screen-features-admin-read-after-write.test.ts`). Echoing the request back
 * would make this endpoint report success identically whether or not the write
 * landed.
 *
 * `schoolSlug` and `name` ride along because `:id` accepts an internal id, a
 * slug or a public id: the answer has to name the school it actually hit.
 */
const SchoolConfigWriteResponseBase = {
  schoolId: z.string().describe('School public id (cuid) — the resolved target, whatever the path named'),
  slug: z.string().describe('School slug, so the operator can confirm which school was written'),
  name: z.string().describe('School display name'),
  config: z
    .record(z.string(), z.unknown())
    .nullable()
    .describe(
      'The merged block after the write (fresh primary read); null when neither default nor override defines it'
    )
}

/** `PATCH /internal/schools/:id/config/global-features/:feature`. */
export const SetSchoolGlobalFeatureResponseSchema = z.object({
  ...SchoolConfigWriteResponseBase,
  feature: z.string().describe('The global-feature key that was patched')
})
export type SetSchoolGlobalFeatureResponse = z.infer<typeof SetSchoolGlobalFeatureResponseSchema>

/** `PATCH /internal/schools/:id/config/screen-features/:screen`. */
export const SetSchoolScreenFeaturesResponseSchema = z.object({
  ...SchoolConfigWriteResponseBase,
  screen: z.string().describe('The screen key that was patched')
})
export type SetSchoolScreenFeaturesResponse = z.infer<typeof SetSchoolScreenFeaturesResponseSchema>
